import {Customer} from '@hconnect/apiclient'
import {Button, GenericInput} from '@hm/fundamental'
import {mdiMagnify} from '@mdi/js'
import {CircularProgress} from '@mui/material'
import {AxiosError} from 'axios'
import React, {useEffect, useState} from 'react'

import {useAdminPilotUser} from '../common/hooks/useAdminPilotUser'
import {RoleCustomerLookupChip} from '../components/Role/RoleCustomerLookupChip'
import {useCustomerByNumber, useCustomerWithoutNumber} from '../hooks/useCustomer'
import {useTranslation} from '../hooks/useTranslation'
import {Permission} from '../modules/LoggedInUser'
import {GroupedRoleAssignment} from '../modules/ManageUsers.selectors'

import {CustomerOrPayerDropdown} from './Roles/CustomerOrPayerDropdown/CustomerOrPayerDropdown'

type RoleCustomerLookupTextFieldProps = {
  customers: Customer[]
  groupedRoleAssignment: GroupedRoleAssignment
  onChangeCustomerIds: (customerIds: string[]) => void
  dataScopeName: string
  name: string
  showCustomerOrPayerDropdown?: boolean
  setShowCustomerOrPayerDropdown: (show: boolean) => void
  customerAdminData?: Record<string, string | undefined>
  loggedInUserPermissions?: Permission[] | null
  helperText?: string
  disabled?: boolean
}

// eslint-disable-next-line complexity
export const RoleCustomerLookupTextField = ({
  onChangeCustomerIds,
  customers,
  helperText,
  groupedRoleAssignment,
  dataScopeName,
  customerAdminData,
  setShowCustomerOrPayerDropdown,
  loggedInUserPermissions,
  ...props
}: RoleCustomerLookupTextFieldProps) => {
  const {
    variantDataScopes: {payerIds}
  } = groupedRoleAssignment
  const {t} = useTranslation()

  const [textFieldValue, setTextFieldValue] = useState<string>('')
  const {countryId, orgUnitId, businessLine} = groupedRoleAssignment.commonDataScopes
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)

  const customerAdminQueryInfo = useCustomerWithoutNumber(
    customerAdminData?.countryId || '',
    customerAdminData?.orgUnitId || '',
    customerAdminData?.businessLine || '',
    !!customerAdminData && groupedRoleAssignment.variantDataScopes.customersScopes.length === 0
  )

  const customerQueryInfo = useCustomerByNumber(
    textFieldValue,
    countryId as string,
    orgUnitId as string,
    businessLine as string,
    false
  )

  const newCustomers = customerQueryInfo.data
  const newCustomerAdminCustomers = customerAdminQueryInfo.data
  const errorMessage = (
    (customerAdminQueryInfo.error as AxiosError) || (customerQueryInfo.error as AxiosError)
  )?.message

  const noResultError =
    newCustomers?.length === 0 ? t(`roleAssignment.noLookupResult.${dataScopeName}`) : ''

  const handleLookup = async () => customerQueryInfo.refetch()

  useEffect(() => {
    if (!isUserAdminPilot && newCustomers && newCustomers.length > 0) {
      onChangeCustomerIds(newCustomers.map((customer) => customer.customerId))
      customerQueryInfo.remove()
    }

    newCustomerAdminCustomers &&
      setShowCustomerOrPayerDropdown(newCustomerAdminCustomers?.length > 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCustomers, newCustomerAdminCustomers])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    void handleLookup()
  }

  const handleAddCustomer = (value: string) => {
    newCustomerAdminCustomers &&
      newCustomerAdminCustomers.length > 0 &&
      onChangeCustomerIds(
        newCustomerAdminCustomers
          .filter((customer) => customer.customerId === value)
          .map((value) => value.customerId)
      )
    setShowCustomerOrPayerDropdown(false)
  }

  // don't allow to enter customers if payers exist
  const disabled = (payerIds && payerIds.length > 0) || props.disabled
  const hidden =
    !customerAdminQueryInfo.isFetching &&
    isUserAdminPilot &&
    (groupedRoleAssignment.variantDataScopes.customersScopes.length > 0 ||
      !newCustomerAdminCustomers)

  if (customerAdminQueryInfo.isFetching) {
    return <CircularProgress style={{margin: '8px 0'}} />
  }

  if (hidden) {
    return null
  }

  return (
    <>
      {!isUserAdminPilot ? (
        <div className="flex gap-2 w-full max-w-[600px]">
          <GenericInput
            {...props}
            label={t(`roleAssignment.dataScope.${dataScopeName}`)}
            value={disabled ? '' : textFieldValue}
            onChange={(value) => setTextFieldValue(value)}
            onKeyDown={handleKeyDown}
            error={errorMessage || helperText || noResultError}
            type="search"
            isDisabled={disabled}
            data-cy="customer-number-lookup"
            isReadOnly={customerQueryInfo.isInitialLoading}
            isTouched={true}
            className="flex-1 text-nowrap"
          />
          <Button
            data-cy="button-customer-lookup"
            variant="secondary"
            type="button"
            icon={
              customerAdminQueryInfo.isInitialLoading || customerQueryInfo.isInitialLoading ? (
                <CircularProgress size={16} />
              ) : (
                mdiMagnify
              )
            }
            disabled={!textFieldValue || disabled}
            className="h-auto max-h-12"
            onClick={() => {
              void handleLookup()
            }}
          >
              {t(`roleAssignment.customerVerify`)}
          </Button>
        </div>
      ) : (
        <CustomerOrPayerDropdown
          dataScope="customerIds"
          name="customerSelect"
          onChange={handleAddCustomer}
          disabled={disabled}
          options={
            customerAdminQueryInfo.data?.map((data) => ({
              label: data.customerNumber || '',
              subLabel: data.customerName || '',
              value: data.customerId || ''
            })) || []
          }
          loggedInUserPermissions={loggedInUserPermissions}
        />
      )}
      {customers.length > 0 && (
        <div>
          {customers.map((customer: Customer) => {
            const {customerId} = customer
            return <RoleCustomerLookupChip key={customerId} customerId={customerId} />
          })}
        </div>
      )}
    </>
  )
}
